/**
 * Domain constants for the cattle trade.
 *
 * RULE: the database stores CODES (e.g. `DANT_4`), never labels ("4 Dant").
 * The English labels here are the fallback and the seed for the dictionary in
 * `i18n.ts`, which is what the interface actually renders. This is what makes
 * adding Urdu a dictionary file rather than a data migration.
 */

// ---------------------------------------------------------------------------
// Age by teeth — the Pakistani convention. This is the primary age field.
// There is deliberately no date-of-birth field: traders do not know it and
// do not use it. Age is judged by counting permanent incisors.
// ---------------------------------------------------------------------------

export const TEETH_AGES = [
  { code: 'KHEERA', label: 'Kheera', urdu: 'کھیرا', incisors: 0, approxAge: 'Under 2 years' },
  { code: 'DANT_2', label: '2 Dant', urdu: '۲ دانت', incisors: 2, approxAge: 'About 2 years' },
  { code: 'DANT_4', label: '4 Dant', urdu: '۴ دانت', incisors: 4, approxAge: '2.5 – 3 years' },
  { code: 'DANT_6', label: '6 Dant', urdu: '۶ دانت', incisors: 6, approxAge: '3 – 3.5 years' },
  { code: 'DANT_8', label: '8 Dant', urdu: '۸ دانت', incisors: 8, approxAge: '4+ years (full mouth)' },
] as const

export type TeethAgeCode = (typeof TEETH_AGES)[number]['code']

export const TEETH_AGE_CODES = TEETH_AGES.map((t) => t.code) as unknown as [
  TeethAgeCode,
  ...TeethAgeCode[],
]

const TEETH_BY_CODE = new Map(TEETH_AGES.map((t) => [t.code, t]))

export function teethLabel(code: TeethAgeCode | null | undefined): string {
  if (!code) return '—'
  return TEETH_BY_CODE.get(code)?.label ?? code
}

/** Number of permanent incisors, used for sorting "youngest first". */
export function teethIncisors(code: TeethAgeCode | null | undefined): number {
  if (!code) return -1
  return TEETH_BY_CODE.get(code)?.incisors ?? -1
}

// ---------------------------------------------------------------------------
// Sex
// ---------------------------------------------------------------------------

export const SEXES = [
  { code: 'BULL', label: 'Bull' },
  { code: 'COW', label: 'Cow' },
  { code: 'BULL_CALF', label: 'Bull Calf' },
  { code: 'HEIFER', label: 'Heifer' },
] as const

export type SexCode = (typeof SEXES)[number]['code']
export const SEX_CODES = SEXES.map((s) => s.code) as unknown as [SexCode, ...SexCode[]]

// ---------------------------------------------------------------------------
// Breeds — Pakistani stock
// ---------------------------------------------------------------------------

export const BREEDS = [
  { code: 'SAHIWAL', label: 'Sahiwal' },
  { code: 'CHOLISTANI', label: 'Cholistani' },
  { code: 'NILI_RAVI', label: 'Nili-Ravi (Buffalo)' },
  { code: 'KUNDI', label: 'Kundi (Buffalo)' },
  { code: 'KANKREJ', label: 'Kankrej' },
  { code: 'RED_SINDHI', label: 'Red Sindhi' },
  { code: 'DHANNI', label: 'Dhanni' },
  { code: 'BHAGNARI', label: 'Bhagnari' },
  { code: 'DAJAL', label: 'Dajal' },
  { code: 'THARPARKAR', label: 'Tharparkar' },
  { code: 'ACHAI', label: 'Achai' },
  { code: 'ROHI', label: 'Rohi' },
  { code: 'FRIESIAN_CROSS', label: 'Friesian Cross' },
  { code: 'JERSEY_CROSS', label: 'Jersey Cross' },
  { code: 'SAHIWAL_CROSS', label: 'Sahiwal Cross' },
  { code: 'AUSTRALIAN', label: 'Australian / Imported' },
  { code: 'DESI', label: 'Desi' },
  { code: 'CROSSBREED', label: 'Crossbreed' },
  { code: 'OTHER', label: 'Other' },
] as const

export type BreedCode = (typeof BREEDS)[number]['code']
export const BREED_CODES = BREEDS.map((b) => b.code) as unknown as [BreedCode, ...BreedCode[]]

// ---------------------------------------------------------------------------
// Animal lifecycle
// ---------------------------------------------------------------------------

export const ANIMAL_STATUSES = [
  { code: 'AVAILABLE', label: 'In Stock', tone: 'green' },
  { code: 'RESERVED', label: 'Reserved', tone: 'amber' },
  { code: 'SOLD', label: 'Sold', tone: 'blue' },
  { code: 'DIED', label: 'Died', tone: 'red' },
  { code: 'LOST', label: 'Lost', tone: 'red' },
] as const

export type AnimalStatusCode = (typeof ANIMAL_STATUSES)[number]['code']
export const ANIMAL_STATUS_CODES = ANIMAL_STATUSES.map((s) => s.code) as unknown as [
  AnimalStatusCode,
  ...AnimalStatusCode[],
]

/** An animal still owned — still accruing feed cost, still capital deployed. */
export const HELD_STATUSES: AnimalStatusCode[] = ['AVAILABLE', 'RESERVED']

// ---------------------------------------------------------------------------
// Expense categories
//
// Routine feed is NOT a category. It is a daily rate over a date range (see
// the feed plan in the costing package). The FEED_* categories here are the
// two things that sit either side of that estimate:
//
//   FEED_EXTRA        an explicit top-up the trader chose to log — a fattening
//                     supplement bought for one animal. It ADDS to the estimate.
//   FEED_WANDA/TOORI/ the real bulk bills. A month of wanda REPLACES the
//   KUTTAR/BULK_OTHER estimate for the days it covers, because otherwise the
//                     same feed is counted twice and every profit figure is
//                     roughly a third too high (BR §6).
//
// Suppression is per category, which is why it is a field on the category and
// not a property of the expense: a vet bill or shed rent must never switch the
// feed estimate off.
// ---------------------------------------------------------------------------

export const EXPENSE_CATEGORIES = [
  { code: 'VET_MEDICINE', label: 'Vet / Medicine', side: 'holding', suppressesFeedEstimate: false },
  { code: 'VACCINATION', label: 'Vaccination', side: 'holding', suppressesFeedEstimate: false },
  {
    code: 'FEED_EXTRA',
    label: 'Extra Feed (beyond daily rate)',
    side: 'holding',
    suppressesFeedEstimate: false,
  },
  { code: 'FEED_WANDA', label: 'Wanda', side: 'holding', suppressesFeedEstimate: true },
  { code: 'FEED_TOORI', label: 'Toori', side: 'holding', suppressesFeedEstimate: true },
  { code: 'FEED_KUTTAR', label: 'Kuttar', side: 'holding', suppressesFeedEstimate: true },
  { code: 'FEED_BULK_OTHER', label: 'Other bulk feed', side: 'holding', suppressesFeedEstimate: true },
  { code: 'LABOUR', label: 'Labour / Wages', side: 'holding', suppressesFeedEstimate: false },
  { code: 'SHED_RENT', label: 'Shed Rent', side: 'holding', suppressesFeedEstimate: false },
  { code: 'UTILITIES', label: 'Utilities', side: 'holding', suppressesFeedEstimate: false },
  { code: 'GROOMING', label: 'Grooming', side: 'holding', suppressesFeedEstimate: false },
  { code: 'TRANSPORT', label: 'Transport', side: 'either', suppressesFeedEstimate: false },
  { code: 'MANDI_FEE', label: 'Mandi Fee', side: 'either', suppressesFeedEstimate: false },
  {
    code: 'COMMISSION_DALALI',
    label: 'Commission (Dalali)',
    side: 'either',
    suppressesFeedEstimate: false,
  },
  { code: 'MISC', label: 'Miscellaneous', side: 'holding', suppressesFeedEstimate: false },
] as const

export type ExpenseCategory = (typeof EXPENSE_CATEGORIES)[number]
export type ExpenseCategoryCode = ExpenseCategory['code']
export const EXPENSE_CATEGORY_CODES = EXPENSE_CATEGORIES.map((c) => c.code) as unknown as [
  ExpenseCategoryCode,
  ...ExpenseCategoryCode[],
]

const EXPENSE_CATEGORY_BY_CODE = new Map(EXPENSE_CATEGORIES.map((c) => [c.code, c]))

/** The bulk-feed bills, derived from the table so the two cannot drift apart. */
export type FeedBulkCategoryCode = Extract<
  ExpenseCategory,
  { suppressesFeedEstimate: true }
>['code']

export const FEED_BULK_CATEGORY_CODES = EXPENSE_CATEGORIES.filter(
  (c): c is Extract<ExpenseCategory, { suppressesFeedEstimate: true }> => c.suppressesFeedEstimate,
).map((c) => c.code)

/**
 * Does an expense in this category switch the daily feed estimate off for the
 * days it covers? True only for the bulk-feed bills (BR §6).
 */
export function suppressesFeedEstimate(code: ExpenseCategoryCode): boolean {
  return EXPENSE_CATEGORY_BY_CODE.get(code)?.suppressesFeedEstimate ?? false
}

/**
 * Which side of the trade an expense belongs to.
 * PURCHASE costs are sunk at buy time; SALE costs are deducted from proceeds;
 * HOLDING costs accrue while the animal is owned.
 */
export const EXPENSE_SIDES = ['PURCHASE', 'HOLDING', 'SALE'] as const
export type ExpenseSide = (typeof EXPENSE_SIDES)[number]

// ---------------------------------------------------------------------------
// Users and roles
//
// Two unions, deliberately not one. A tenant role belongs to a user inside one
// business; a platform role belongs to a `users` row with a null tenant. Merged
// into a single union, a tenant OWNER type-checks against a platform guard,
// which is exactly the mistake that would hand a trader the platform console.
// ---------------------------------------------------------------------------

export const ROLES = [
  {
    code: 'OWNER',
    label: 'Owner',
    description: 'Full access. Manages users, settings, backups and can delete records.',
  },
  {
    code: 'PARTNER',
    label: 'Partner / Manager',
    description: 'Sees all money and profit. Cannot manage users, settings or delete records.',
  },
  {
    code: 'STAFF',
    label: 'Staff',
    description: 'Records animals and expenses. Cannot see any prices, profit or reports.',
  },
] as const

export type RoleCode = (typeof ROLES)[number]['code']
export const ROLE_CODES = ROLES.map((r) => r.code) as unknown as [RoleCode, ...RoleCode[]]

export const PLATFORM_ROLES = [
  {
    code: 'PLATFORM_OWNER',
    label: 'Platform Owner',
    description: 'Runs the platform. Plans, billing, suspensions and the danger zone.',
  },
  {
    code: 'PLATFORM_SUPPORT',
    label: 'Platform Support',
    description: 'Reads a business to help with a problem. Cannot bill, suspend or delete.',
  },
] as const

export type PlatformRoleCode = (typeof PLATFORM_ROLES)[number]['code']
export const PLATFORM_ROLE_CODES = PLATFORM_ROLES.map((r) => r.code) as unknown as [
  PlatformRoleCode,
  ...PlatformRoleCode[],
]

/**
 * Can this role see money — purchase price, sale price, cost basis,
 * break-even, profit, the daily feed rate, and all reports?
 *
 * This single predicate gates every financial figure in the app. It is
 * enforced server-side; money is omitted from the payload entirely for
 * roles that return false, never merely hidden with CSS.
 */
export function canSeeMoney(role: RoleCode): boolean {
  return role === 'OWNER' || role === 'PARTNER'
}

/** Can this role manage users, settings, backups, audit log and hard deletes? */
export function canAdminister(role: RoleCode): boolean {
  return role === 'OWNER'
}

// ---------------------------------------------------------------------------
// Payment methods
// ---------------------------------------------------------------------------

export const PAYMENT_METHODS = [
  { code: 'CASH', label: 'Cash' },
  { code: 'BANK', label: 'Bank Transfer' },
  { code: 'EASYPAISA', label: 'Easypaisa' },
  { code: 'JAZZCASH', label: 'JazzCash' },
  { code: 'CHEQUE', label: 'Cheque' },
  { code: 'OTHER', label: 'Other' },
] as const

export type PaymentMethodCode = (typeof PAYMENT_METHODS)[number]['code']
export const PAYMENT_METHOD_CODES = PAYMENT_METHODS.map((m) => m.code) as unknown as [
  PaymentMethodCode,
  ...PaymentMethodCode[],
]

// ---------------------------------------------------------------------------
// Media
// ---------------------------------------------------------------------------

export const MEDIA_KINDS = ['PHOTO', 'VIDEO'] as const
export type MediaKind = (typeof MEDIA_KINDS)[number]

export const MEDIA_STATUSES = ['UPLOADING', 'PROCESSING', 'READY', 'FAILED'] as const
export type MediaStatus = (typeof MEDIA_STATUSES)[number]

/**
 * Hard limits enforced on both client and server.
 *
 * The owner set the upload caps on 11 September 2026: a photo is compressed on
 * the phone and may not exceed 5 MB; a video may not exceed 20 MB. They were 25
 * and 200 — sized for a local disk, not for a cloud bucket billed per GB and a
 * trader on mandi mobile data.
 */
export const MEDIA_LIMITS = {
  photoMaxBytes: 5 * 1024 * 1024,
  videoMaxBytes: 20 * 1024 * 1024,
  videoWarnBytes: 15 * 1024 * 1024,
  videoMaxSeconds: 180,
  /**
   * Videos larger than this are transcoded to 720p H.264. Kept below
   * `videoMaxBytes` so the step still runs — at the old 40 MB it would now
   * never fire.
   */
  videoTranscodeOverBytes: 10 * 1024 * 1024,
} as const
